import { ref, reactive, computed, watch } from "vue";
import { fabric } from "fabric";
import { store } from "@/store";

import { undoActionStack } from "@/canvas-api/ActionStack";
import { arrangeObjectStack } from "@/canvas-api/ArrangeObjectStack";
import { attachEventListeners } from "@/canvas-api/EventListeners";
import { resizeGridLines } from "@/canvas-api/GridLines";
import { loadJson, loadOldEditorJson } from "@/canvas-api/LoadJson";
import { loadIcons, hideIcons } from "@/canvas-api/ObjectIcons";
import { updateActiveObject } from "@/canvas-api/UpdateActiveObject";
import { centerCanvasViewPort, centerAndSizeScrollers } from "@/canvas-api/ViewPort";
import { zoomValue, zoomOnMount, isZooming } from "@/canvas-api/Zoom";
import useDrawLabel from "@/composables/useDrawLabel";
import { handleGridLines } from "@/canvas-api/GridLines";

import useMountCanvas from "@/composables/useMountCanvas";
import { rotateCanvas } from "@/composables/useRotateCanvas";

import { CanvasDimensions, Dimensions, CurrentLabel, Property, SheetLabel } from "@/types";

const initialize = computed<string>(() => store.getters.initialize);
const mountingLabel = computed<boolean>(() => store.getters.mountingLabel);
const userArtwork = computed<any>(() => store.getters.userArtworkFetch.artwork);
const template = computed<any>(() => store.getters.templateAndLabel.template);

const labelDimensions = computed<Dimensions>(() => store.getters.dimensions);
const canvasDimensions = computed<CanvasDimensions>(() => store.getters.canvasDimensions);
const canvasOrientation = computed<string>(() => store.getters.canvasOrientation);

const currentLabel = computed<CurrentLabel | SheetLabel>(() => store.getters.currentLabel);
const currentLabelBgColor = computed<string>(() => store.getters.currentLabelBgColor);

// const currentActiveObject = computed<fabric.Object>(() => store.getters.currentActiveObject);

export const canvasWrap = ref<HTMLDivElement>(); //App.vue ref
export const canvasRef = ref<HTMLCanvasElement>(); //Canvas.vue ref

//Add unique ids to all objects that get added to the canvas so that we can keep track of state changes easier in action stack
//Fabric types do not inherently come with an id property so we need to extend and make custom interfaces
export const objectId = ref(1);

export const canvas = ref<fabric.Canvas>(new fabric.Canvas(null));
export const canvasWidth = ref(0);
export const canvasHeight = ref(0);

export const canvasReactive = reactive<any>({}); //This is for storing functions used in Canvas.vue...might change in future

export const mountMainCanvas = async (
  canvasWrapWidth: number,
  canvasWrapHeight: number
): Promise<void> => {
  canvasWidth.value = canvasWrapWidth;
  canvasHeight.value = canvasWrapHeight;

  canvas.value = useMountCanvas(
    canvas.value,
    canvasRef.value,
    canvasWidth.value,
    canvasHeight.value,
    "#edf0f2"
  );

  //Prevents users from group selecting with holding left mouse click and dragging
  //Can implement in future, but causes canvas event listener issues and doesn't work will with
  //current gridLine system
  canvas.value.selection = false;

  //Prevent selected objects from being automatically moved to front
  canvas.value.preserveObjectStacking = true;

  attachEventListeners(canvas.value);
  await useDrawLabel(canvas.value);
  await loadArtwork();
  await loadIcons(canvas.value);

  const elem = canvasWrap.value as HTMLDivElement; //This is checked and securd by v-if="canvasWrap" on Canvas component in App.vue
  const offSet = labelDimensions.value.units.offSetPx;

  if (elem.clientWidth !== undefined && elem.clientHeight !== undefined) {
    centerCanvasViewPort();
    zoomOnMount(canvas.value, elem.clientWidth, elem.clientHeight, offSet);
    handleGridLines(canvas.value);
    centerAndSizeScrollers();
  }

  arrangeObjectStack(canvas.value);
};

const loadArtwork = async () => {
  if (initialize.value === "template" || initialize.value === "userArtwork") {
    if (
      (userArtwork.value && Number(userArtwork.value.editorvue)) ||
      (template.value && Number(template.value.editorvue))
    ) {
      objectId.value = await loadJson(canvas.value, objectId.value);
    } else {
      objectId.value = await loadOldEditorJson(canvas.value, objectId.value);
    }
  }
};

const removeNonUserObjects = () => {
  const canvasObjects = canvas.value.getObjects();

  let i = 0;
  while (canvasObjects[i]) {
    if (
      canvasObjects[i].name === "labelCanvas" ||
      canvasObjects[i].name === "actualLabelSize" ||
      canvasObjects[i].name === "safeZone" ||
      canvasObjects[i].name === "bleedLine" ||
      canvasObjects[i].name === "bleedLineTop" ||
      canvasObjects[i].name === "snapLineGroup"
    ) {
      canvas.value.remove(canvasObjects[i]);
    }

    i++;
  }
};

/* 
  CANVAS WATCHERS
*/
watch(currentLabel, async () => {
  //Avoid watcher running when initial label request is received when main canvas is mounting
  //canvasWrap is null when server hotloads during development, just added for convience here
  if (mountingLabel.value || !canvasWrap.value) {
    return;
  }

  removeNonUserObjects();

  await useDrawLabel(canvas.value);

  const elem = canvasWrap.value as HTMLDivElement; //This is checked and securd by v-if="canvasWrap" on Canvas component in App.vue
  const offSet = labelDimensions.value.units.offSetPx;

  if (elem.clientWidth !== undefined && elem.clientHeight !== undefined) {
    centerCanvasViewPort();
    zoomOnMount(canvas.value, elem.clientWidth, elem.clientHeight, offSet);
    handleGridLines(canvas.value);
    centerAndSizeScrollers();
  }

  canvasReactive.updateGuidelinesModalBtn();

  arrangeObjectStack(canvas.value);

  canvas.value.renderAll();
});

watch(canvasOrientation, async (orientation) => {
  if (mountingLabel.value) {
    return;
  }

  hideIcons();
  removeNonUserObjects();
  canvasReactive.setShowStackPlacement(false);

  await useDrawLabel(canvas.value);

  rotateCanvas(canvas.value, orientation);

  const elem = canvasWrap.value as HTMLDivElement;
  const offSet = labelDimensions.value.units.offSetPx;

  if (elem.clientWidth !== undefined && elem.clientHeight !== undefined) {
    centerCanvasViewPort();
    zoomOnMount(canvas.value, elem.clientWidth, elem.clientHeight, offSet);
    handleGridLines(canvas.value);
    centerAndSizeScrollers();
  }

  canvasReactive.updateGuidelinesModalBtn();

  arrangeObjectStack(canvas.value);
});

watch(isZooming, (isZooming) => {
  // These are just actions that should be preformed whenever a change in zoom value occurs
  if (isZooming) {
    canvasReactive.setShowStackPlacement(false);
  } else {
    resizeGridLines(canvas.value);
  }
});

watch(zoomValue, (newVal) => {
  if (mountingLabel.value) {
    return;
  }

  centerCanvasViewPort();

  const canvasCenter = canvas.value.getCenter();
  const point: fabric.Point = new fabric.Point(canvasCenter.left, canvasCenter.top);
  canvas.value.zoomToPoint(point, newVal / 100);

  centerAndSizeScrollers();
});

watch(currentLabelBgColor, (newColor) => {
  if (newColor) {
    canvas.value._objects.forEach((obj) => {
      if (obj.name === "labelCanvas") {
        obj.set({
          fill: newColor,
          dirty: true,
        });
      }
    });

    canvas.value.renderAll();
  }
});

/* 
  CANVAS WATCHERS END
*/

/*
 OBJECTS
*/
export const addUserObject = (object: fabric.Object, left?: number, top?: number) => {
  object.id = objectId.value;
  objectId.value += 1;

  if (left !== undefined && top !== undefined) {
    const location = { left, top };

    Object.assign(object, location);
  } else {
    const { left, top } = canvas.value.getCenter();

    const location = { left, top };

    Object.assign(object, location);
  }

  undoActionStack.push({ id: object.id, type: "add" });

  let position = 1; // At least always above labelCanvas in object stack
  canvas.value.getObjects().forEach((obj: fabric.Object, idx: number) => {
    if (!Number.isNaN(Number(obj.id))) {
      if (obj.id >= position) {
        position = idx + 1;
      }
    }
  });

  canvas.value.add(object);
  canvas.value.setActiveObject(object);
  canvas.value.moveTo(object, position);
};

export const removeActiveObject = () => {
  canvas.value.remove(canvas.value.getActiveObject());
};

export const getObjectProperty = (property: Property) => {
  updateActiveObject(canvas.value.getActiveObject(), property);
  canvas.value.renderAll();
};
/*
 OBJECTS END
*/

// export const centerWindowViewport = (
//   elem: HTMLDivElement,
//   canvasWidth: number,
//   canvasHeight: number
// ): void => {
//   if (elem.clientWidth && elem.clientHeight) {
//     elem.scrollLeft = (canvasWidth - elem.clientWidth) / 2;
//     elem.scrollTop = (canvasHeight - elem.clientHeight) / 2;
//   }
// };
